import {
  Box,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Switch,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { findLast } from "lodash";
import moment from "moment";
import { useState } from "react";
import {
  FiArrowRight,
  FiBarChart2,
  FiDownload,
  FiEdit2,
  FiExternalLink,
  FiFileText,
  FiMoreVertical,
  FiRefreshCw,
  FiSearch,
  FiShare2,
  FiTrash2,
} from "react-icons/fi";
import { LuArrowDown, LuArrowDownUp, LuArrowUp } from "react-icons/lu";
import { SpotlightOverlay } from "../../../common/SpotlightOverlay";
import CustomTooltip from "../../../common/Tooltip";
import CustomPagination from "../../../components/Common/CustomPagination";

const useStyles = makeStyles({
  darkerUnchecked: {
    color: "#cccccc",
  },
});

/* ─── Main component ─────────────────────────────────────────────── */
function MyOrdersTable(props) {
  const {
    loading = false,
    loader = false,
    isInitialLoading = false,
    allClientOrders = [],
    onClickEditQuery = () => { },
    onChangePinQuery = () => { },
    onStepperQuery = () => { },
    onDeleteQuery = () => { },
    onChangePagination = () => { },
    filters = {},
    onClickSaveCargo = () => { },
    onClickSaveCargoVessel = () => { },
    onCargoAnalysisClick = () => { },
    handleOpenCargoContractModel = () => { },
    onExportPdf = () => { },
    handleTourAction = () => { },
    handleActionComplete = () => { },
    onChangeSort = () => { },
    onClickClear = () => { },
    hasFetched = false,
  } = props;
  const isLoading = loading || loader;
  const sortBy = filters?.sortBy || "";
  const sortOrder = filters?.sortDirection || "";

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [flashStepperId, setFlashStepperId] = useState(null);
  const [spotlightItemId, setSpotlightItemId] = useState(null);
  const classes = useStyles();
  const COL = {
    no: { label: "No.", width: 36, align: "center" },
    matrix: { label: "Add In Matrix", width: 105, align: "center" },
    name: {
      label: "Cargo Name",
      width: "18%",
      minWidth: 140,
      align: "left",
      sortable: true,
      sortKey: "name",
    },
    route: { label: "Route", width: "24%", minWidth: 170, align: "left" },
    qty: { label: "Qty (MT)", width: "9%", minWidth: 80, align: "right" },
    account: { label: "Account", width: "11%", minWidth: 90, align: "left" },
    createdDate: {
      label: "Created On",
      width: "12%",
      minWidth: 100,
      align: "left",
      sortable: true,
      sortKey: "createdAt",
    },
    createdBy: {
      label: "Created By",
      width: "12%",
      minWidth: 100,
      align: "left",
    },
    actions: { label: "Actions", width: 90, minWidth: 90, align: "center" },
  };

  const handleSortClick = (col) => {
    if (!col.sortable || !col.sortKey) return;

    let newSortOrder = "asc";
    if (sortBy === col.sortKey) {
      if (sortOrder === "asc") newSortOrder = "desc";
      else if (sortOrder === "desc") {
        newSortOrder = "";
      }
    }

    // If no sort order, we reset sortBy as well
    if (!newSortOrder) {
      onChangeSort("", "");
    } else {
      onChangeSort(col.sortKey, newSortOrder);
    }
  };

  const thStyle = {
    padding: "5px 6px",
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: "0.02em",
    color: "#334155",
    textTransform: "capitalize",
    borderBottom: "1px solid #cbd5e1",
    whiteSpace: "nowrap",
    background: "#e2e8f0",
  };

  const tdStyle = {
    padding: "0.5px 6px",
    fontSize: 11,
    color: "#1e293b",
    borderBottom: "1px solid #e2e8f0",
    verticalAlign: "middle",
    lineHeight: "1.35",
  };

  return (
    <div
      style={{
        marginTop: "0.375rem",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 0,
          border: "1px solid #cbd5e1",
          overflow: "hidden",
          minHeight:
            isInitialLoading && !allClientOrders.length ? "500px" : "auto",
        }}
        className="shadow-sm"
      >
        {filters?.totalPages > filters?.limit && (
          <div
            style={{
              borderBottom: "1px solid #f1f5f9",
              display: "flex",
              justifyContent: "flex-end",
              padding: "1px",
            }}
          >
            <CustomPagination
              onChangePagination={onChangePagination}
              filters={filters}
              totalPage={filters?.totalPages}
            />
          </div>
        )}

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#e2e8f0" }}>
                {Object.values(COL).map((col) => (
                  <th
                    key={col.label}
                    style={{
                      ...thStyle,
                      width: col.width,
                      minWidth: col.minWidth,
                      textAlign: col.align || "left",
                      cursor: col.sortable ? "pointer" : "default",
                    }}
                    onClick={() => handleSortClick(col)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                          col.align === "right"
                            ? "flex-end"
                            : col.align === "center"
                              ? "center"
                              : "flex-start",
                        gap: 4,
                      }}
                    >
                      {col.label}
                      {col.sortable && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            color:
                              sortBy === col.sortKey ? "#0f172a" : "#64748b",
                          }}
                        >
                          {sortBy === col.sortKey && sortOrder === "desc" ? (
                            <LuArrowDown size={13} />
                          ) : sortBy === col.sortKey && sortOrder === "asc" ? (
                            <LuArrowUp size={13} />
                          ) : (
                            <LuArrowDownUp size={13} />
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {isInitialLoading &&
                (!Array.isArray(allClientOrders) || !allClientOrders.length) ? (
                <tr>
                  <td
                    colSpan={Object.keys(COL).length}
                    style={{
                      textAlign: "center",
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="440px"
                    >
                      {allClientOrders?.length > 0 && <CircularProgress />}
                    </Box>
                  </td>
                </tr>
              ) : allClientOrders.length ? (
                allClientOrders.map((item, index) => {
                  const firstLoad = item.cargoList?.find(
                    (c) => c.operation === "Loading",
                  );
                  const lastDischarge = findLast(item.cargoList, {
                    operation: "Discharging",
                  });
                  const isMenuOpen =
                    Boolean(anchorEl) && activeItem?._id === item._id;

                  return (
                    <tr
                      key={item._id || index}
                      data-spotlight={
                        index === 0 ? "view-cargo-row" : undefined
                      }
                      onDoubleClick={() => onClickEditQuery(item)}
                      style={{
                        transition: "background 0.15s",
                        cursor: "pointer",
                        backgroundColor:
                          index % 2 === 1 ? "#f8fafc" : "#ffffff",
                      }}
                      className="hover:bg-slate-100/90 transition-colors"
                    >
                      <td
                        style={{
                          ...tdStyle,
                          color: "#64748b",
                          fontWeight: 500,
                          textAlign: "center",
                        }}
                      >
                        {(filters.offset - 1) * filters.limit + index + 1 ||
                          index + 1}
                      </td>

                      <td
                        style={{
                          ...tdStyle,
                          textAlign: "center",
                          backgroundColor:
                            flashStepperId === item._id
                              ? "rgba(16, 63, 107, 0.05)"
                              : "transparent",
                          transition: "background-color 0.4s ease",
                        }}
                      >
                        {item?.isAllStepClear ? (
                          <Switch
                            data-spotlight="show-in-matrix-btn"
                            data-order-id={item._id}
                            checked={item?.isShowInCalculator}
                            onChange={() => {
                              onChangePinQuery(item);
                              handleTourAction("matrix");
                            }}
                            color="primary"
                            size="small"
                          />
                        ) : (
                          <CustomTooltip
                            position="top"
                            text="Complete steps to show in calculator"
                          >
                            <button
                              data-spotlight={`stepper-btn-${item._id}`}
                              onClick={() => {
                                onStepperQuery(item._id);
                                handleTourAction("stepper");
                                handleActionComplete("stepper");
                              }}
                              style={{
                                background:
                                  flashStepperId === item._id ||
                                    spotlightItemId === item._id
                                    ? "rgba(16, 63, 107, 0.12)"
                                    : "none",
                                border: "none",
                                cursor: "pointer",
                                color: "#313C4C",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "50%",
                                width: 24,
                                height: 24,
                                transition: "all 0.4s ease",
                                transform:
                                  flashStepperId === item._id ||
                                    spotlightItemId === item._id
                                    ? "scale(1.15)"
                                    : "scale(1)",
                              }}
                            >
                              <FiRefreshCw
                                size={14}
                                style={{
                                  color: "#313C4C",
                                  transition: "transform 0.4s ease",
                                  transform:
                                    flashStepperId === item._id ||
                                      spotlightItemId === item._id
                                      ? "rotate(180deg)"
                                      : "rotate(0deg)",
                                }}
                              />
                            </button>
                          </CustomTooltip>
                        )}
                      </td>

                      <td style={{ ...tdStyle, color: "#1e293b" }}>
                        {item.name || "—"}
                      </td>

                      <td style={tdStyle}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            color: "#1e293b",
                          }}
                        >
                          <span style={{ fontWeight: 500 }}>
                            {firstLoad?.port || "—"}
                          </span>
                          <FiArrowRight size={13} color="#94a3b8" />
                          <span style={{ fontWeight: 500 }}>
                            {lastDischarge?.port || "—"}
                          </span>
                        </div>
                      </td>

                      <td
                        style={{
                          ...tdStyle,
                          textAlign: "right",
                          fontWeight: 500,
                          color: "#1e293b",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {firstLoad?.cargoQty
                          ? Number(firstLoad.cargoQty).toLocaleString()
                          : "—"}
                      </td>

                      <td style={{ ...tdStyle, color: "#1e293b" }}>
                        {firstLoad?.account || "—"}
                      </td>

                      <td style={{ ...tdStyle, color: "#1e293b" }}>
                        {item.createdDate
                          ? moment(item.createdDate).format("DD MMM YYYY")
                          : "—"}
                      </td>

                      <td style={{ ...tdStyle, color: "#1e293b" }}>
                        {item.createdUser.firstName +
                          " " +
                          item.createdUser.lastName || "—"}
                      </td>

                      <td style={{ ...tdStyle, textAlign: "center", width: 90, minWidth: 90 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            position: "relative",
                            width: "100%",
                          }}
                        >
                          <button
                            data-spotlight={
                              index === 0 ? "view-cargo-btn" : undefined
                            }
                            onClick={() => {
                              onClickEditQuery(item);
                              handleTourAction("view");
                            }}
                            style={iconBtnStyle}
                            title="Edit"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = "#1d4ed8";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = "#000";
                            }}
                          >
                            <FiEdit2 size={14} style={{ display: "block" }} />
                          </button>

                          <button
                            onClick={() => onDeleteQuery(item)}
                            style={{ ...iconBtnStyle, color: "#ef4444" }}
                            title="Delete"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = "#dc2626";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = "#ef4444";
                            }}
                          >
                            <FiTrash2 size={14} style={{ display: "block" }} />
                          </button>

                          <div style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                            <button
                              data-spotlight="more-options-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAnchorEl(e.currentTarget);
                                setActiveItem(item);
                                handleTourAction("more");
                                handleActionComplete("more");
                              }}
                              style={iconBtnStyle}
                              title="More"
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = "#374151";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = "#000";
                              }}
                            >
                              <FiMoreVertical size={14} style={{ display: "block" }} />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : hasFetched ? (
                <tr>
                  <td
                    colSpan={Object.keys(COL).length}
                    style={{
                      textAlign: "center",
                      padding: "48px 0",
                      color: "#94a3b8",
                      fontSize: 14,
                    }}
                  >
                    No cargo orders found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {filters?.totalPages > filters?.limit && (
          <div
            style={{
              borderTop: "1px solid #f1f5f9",
              display: "flex",
              justifyContent: "flex-end",
              padding: "1px",
            }}
          >
            <CustomPagination
              onChangePagination={onChangePagination}
              filters={filters}
              totalPage={filters?.totalPages}
            />
          </div>
        )}
      </div>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setActiveItem(null);
        }}
        PaperProps={{
          style: {
            borderRadius: 8,
            boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
            border: "1px solid #e2e8f0",
            minWidth: 200,
            padding: 0,
          },
        }}
        MenuListProps={{ style: { padding: 0 } }}
      >
        <MenuItem
          onClick={() => {
            onClickSaveCargo(activeItem);
            setAnchorEl(null);
            setActiveItem(null);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            minHeight: 32,
            color: "#374151",
            padding: "5px 12px",
          }}
        >
          <FiExternalLink size={14} color="#000" />
          Create Cargo Cargo Est.
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (activeItem?.isAllStepClear) {
              onClickSaveCargoVessel(activeItem?._id, activeItem);
              setAnchorEl(null);
              setActiveItem(null);
            } else {
              const targetId = activeItem._id;
              setSpotlightItemId(targetId);
              setFlashStepperId(targetId);
              setAnchorEl(null);
              setActiveItem(null);
              setTimeout(() => {
                setSpotlightItemId((prev) => (prev === targetId ? null : prev));
                setFlashStepperId((prev) => (prev === targetId ? null : prev));
              }, 4000);
            }
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            minHeight: 32,
            color: activeItem?.isAllStepClear ? "#374151" : "#94a3b8",
            cursor: activeItem?.isAllStepClear ? "pointer" : "not-allowed",
            padding: "5px 12px",
          }}
        >
          <CustomTooltip
            open={Boolean(activeItem && !activeItem.isAllStepClear)}
            position="top"
            text="Complete Matrix steps to show in calculator"
          >
            <div className="flex align-items-center gap-2">
              <FiBarChart2
                size={14}
                color={activeItem?.isAllStepClear ? "#000" : "#94a3b8"}
              />
              Cargo Vessel Estimation
            </div>
          </CustomTooltip>
        </MenuItem>
        <MenuItem
          onClick={() => {
            onCargoAnalysisClick(activeItem?._id, activeItem);
            setAnchorEl(null);
            setActiveItem(null);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            minHeight: 32,
            color: "#374151",
            padding: "5px 12px",
          }}
        >
          <FiSearch size={14} color="#000" />
          Create Cargo Analysis
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (activeItem?.isAllStepClear) {
              handleOpenCargoContractModel(activeItem);
              setAnchorEl(null);
              setActiveItem(null);
            } else {
              const targetId = activeItem._id;
              setSpotlightItemId(targetId);
              setFlashStepperId(targetId);
              setAnchorEl(null);
              setActiveItem(null);
              setTimeout(() => {
                setSpotlightItemId((prev) => (prev === targetId ? null : prev));
                setFlashStepperId((prev) => (prev === targetId ? null : prev));
              }, 4000);
            }
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            minHeight: 32,
            color: activeItem?.isAllStepClear ? "#374151" : "#94a3b8",
            cursor: activeItem?.isAllStepClear ? "pointer" : "not-allowed",
            padding: "5px 12px",
          }}
        >
          <CustomTooltip
            open={Boolean(activeItem && !activeItem.isAllStepClear)}
            position="top"
            text="Complete Matrix steps to show in calculator"
          >
            <div className="flex align-items-center gap-2">
              <FiFileText
                size={14}
                color={activeItem?.isAllStepClear ? "#000" : "#94a3b8"}
              />
              Create Cargo Contract
            </div>
          </CustomTooltip>
        </MenuItem>
        <MenuItem
          onClick={() => {
            onExportPdf(activeItem, "download");
            setAnchorEl(null);
            setActiveItem(null);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            minHeight: 32,
            color: "#374151",
            padding: "5px 12px",
          }}
        >
          <FiDownload size={14} color="#000" />
          Download PDF
        </MenuItem>
        <MenuItem
          onClick={() => {
            onExportPdf(activeItem, "share");
            setAnchorEl(null);
            setActiveItem(null);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            minHeight: 32,
            color: "#374151",
            padding: "5px 12px",
          }}
        >
          <FiShare2 size={14} color="#000" />
          Share PDF
        </MenuItem>
        <Divider style={{ margin: 0, backgroundColor: "#f1f5f9" }} />
        <MenuItem
          onClick={() => {
            onDeleteQuery(activeItem);
            setAnchorEl(null);
            setActiveItem(null);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            minHeight: 32,
            color: "#ef4444",
            padding: "5px 12px",
          }}
        >
          <FiTrash2 size={14} />
          Delete
        </MenuItem>
      </Menu>
      {spotlightItemId && (
        <SpotlightOverlay
          targetSelector={`[data-spotlight="stepper-btn-${spotlightItemId}"]`}
          active={Boolean(spotlightItemId)}
          onDismiss={() => {
            setSpotlightItemId(null);
            setFlashStepperId(null);
          }}
          message="Complete these steps"
          position="bottom"
          targetPadding={4}
          targetRadius={16}
        />
      )}
    </div>
  );
}

const iconBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 20,
  height: 20,
  padding: 0,
  lineHeight: 1,
  borderRadius: 3,
  border: "none",
  background: "transparent",
  color: "#334155",
  cursor: "pointer",
  transition: "background 0.15s, color 0.15s",
};

export default MyOrdersTable;
